import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "@/App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { 
  Wrench,
  ArrowLeft,
  DollarSign,
  Mail,
  Server,
  Users,
  CheckCircle,
  XCircle,
  Save,
  Send,
  Eye,
  EyeOff,
  Database,
  FileText,
  Building2,
  Image,
  Upload,
  Trash2,
  Plus,
  Edit,
  ScrollText
} from "lucide-react";
import { format } from "date-fns";
import MarketConfig from "../components/MarketConfig";
import BrandingPanel from "../components/BrandingPanel";
import RestaurantPicker from "../components/RestaurantPicker";

const USER_ROLES = [
  "SystemAdmin", "Store Manager", "HelpDesk", "Technician", "Vendor",
  "District Manager", "District Operations", "Finance", "President", "Treasurer"
];

const BUSINESS_UNITS = [
  { id: "qnc", name: "QNC" },
  { id: "diner_dfw", name: "DINER DFW" },
  { id: "diner_fl", name: "DINER FL" },
  { id: "textaco", name: "Textaco" },
];

export default function AdminPanel() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("market-config");
  
  // Thresholds
  const [thresholds, setThresholds] = useState({ dm_limit: 50, do_limit: 200 });
  const [thresholdsLoading, setThresholdsLoading] = useState(false);
  
  // Approver Emails (now grouped by role)
  const [approverEmails, setApproverEmails] = useState({});
  const [emailsLoading, setEmailsLoading] = useState(false);
  const [approverDialogOpen, setApproverDialogOpen] = useState(false);
  const [selectedApprover, setSelectedApprover] = useState(null);
  const [editingApprover, setEditingApprover] = useState({ role: "", name: "", email: "" });
  const [deleteApproverDialogOpen, setDeleteApproverDialogOpen] = useState(false);
  const [approverToDelete, setApproverToDelete] = useState(null);
  
  // SMTP
  const [smtpSettings, setSmtpSettings] = useState({
    sender_name: "",
    sender_email: "",
    smtp_host: "smtp.gmail.com",
    smtp_port: 587,
    smtp_username: "",
    smtp_password: "",
  });
  const [showSmtpPassword, setShowSmtpPassword] = useState(false);
  const [smtpLoading, setSmtpLoading] = useState(false);
  
  // Users
  const [pendingUsers, setPendingUsers] = useState([]);
  const [activeUsers, setActiveUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [editingUser, setEditingUser] = useState({ role: "", units: [], default_technician_id: "" });
  const [createUserDialogOpen, setCreateUserDialogOpen] = useState(false);
  const [newUser, setNewUser] = useState({
    full_name: "",
    email: "",
    password: "",
    role: "",
    units: [],
    restaurants: [],
    default_technician_id: "",
  });
  const [creatingUser, setCreatingUser] = useState(false);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  
  // Email Templates
  const [emailTemplates, setEmailTemplates] = useState({});
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState({ subject: "", body: "" });
  
  // Business Units
  const [businessUnits, setBusinessUnits] = useState([]);
  const [unitDialogOpen, setUnitDialogOpen] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [editingUnit, setEditingUnit] = useState({ name: "", full_name: "", color: "#4F46E5", icon: "utensils" });

  useEffect(() => {
    fetchThresholds();
    fetchApproverEmails();
    fetchSmtpSettings();
    fetchUsers();
    fetchEmailTemplates();
    fetchBusinessUnits();
    fetchAvailableRoles();
    fetchTechnicians();
  }, []);

  // Fetch Functions
  const fetchThresholds = async () => {
    try {
      const res = await api.get("/admin/settings/thresholds");
      setThresholds(res.data);
    } catch (error) {
      console.error("Failed to load thresholds");
    }
  };

  const fetchApproverEmails = async () => {
    try {
      const res = await api.get("/admin/approver-emails");
      setApproverEmails(res.data);
    } catch (error) {
      console.error("Failed to load approver emails");
    }
  };

  const fetchTechnicians = async () => {
    try {
      const res = await api.get("/users/technicians");
      setTechnicians(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error("Failed to load technicians");
    }
  };

  const fetchSmtpSettings = async () => {
    try {
      const res = await api.get("/admin/smtp");
      if (res.data && Object.keys(res.data).length > 0) {
        setSmtpSettings(res.data);
      }
    } catch (error) {
      console.error("Failed to load SMTP settings");
    }
  };

  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const [pendingRes, activeRes] = await Promise.all([
        api.get("/admin/users/pending"),
        api.get("/admin/users/active")
      ]);
      setPendingUsers(pendingRes.data);
      setActiveUsers(activeRes.data);
    } catch (error) {
      toast.error("Failed to load users");
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchEmailTemplates = async () => {
    try {
      const res = await api.get("/admin/email-templates");
      setEmailTemplates(res.data);
    } catch (error) {
      console.error("Failed to load email templates");
    }
  };

  const fetchBusinessUnits = async () => {
    try {
      const res = await api.get("/units/all");
      setBusinessUnits(res.data);
    } catch (error) {
      console.error("Failed to load business units");
    }
  };

  const fetchAvailableRoles = async () => {
    try {
      const res = await api.get("/admin/user-roles");
      const roles = res.data?.roles || [];
      if (roles.length) setAvailableRoles(roles);
    } catch {
      // silent — fall back to USER_ROLES
    }
  };

  // Save Functions
  const saveThresholds = async () => {
    setThresholdsLoading(true);
    try {
      await api.put("/admin/settings/thresholds", thresholds);
      toast.success("Thresholds saved successfully");
    } catch (error) {
      toast.error("Failed to save thresholds");
    } finally {
      setThresholdsLoading(false);
    }
  };

  const saveApproverEmails = async () => {
    setEmailsLoading(true);
    try {
      await api.put("/admin/approver-emails", approverEmails);
      toast.success("Approver emails saved successfully");
    } catch (error) {
      toast.error("Failed to save emails");
    } finally {
      setEmailsLoading(false);
    }
  };

  const saveSmtpSettings = async () => {
    setSmtpLoading(true);
    try {
      await api.put("/admin/smtp", smtpSettings);
      toast.success("SMTP settings saved successfully");
    } catch (error) {
      toast.error("Failed to save SMTP settings");
    } finally {
      setSmtpLoading(false);
    }
  };

  const testSmtp = async () => {
    setSmtpLoading(true);
    try {
      await api.post("/admin/smtp/test");
      toast.success("Test email sent successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send test email");
    } finally {
      setSmtpLoading(false);
    }
  };

  // User Management
  const handleEditUser = (targetUser) => {
    setSelectedUser(targetUser);
    setEditingUser({
      role: targetUser.role || "",
      units: targetUser.units || [],
      restaurants: targetUser.restaurants || [],
      default_technician_id: targetUser.default_technician_id || ""
    });
    setUserDialogOpen(true);
  };

  const handleSaveUser = async () => {
    if (!selectedUser) return;
    
    try {
      await api.put(`/admin/users/${selectedUser.id}`, {
        role: editingUser.role,
        units: editingUser.units,
        restaurants: editingUser.restaurants || [],
        default_technician_id: editingUser.default_technician_id || "",
        status: "active"
      });
      toast.success("User updated successfully");
      setUserDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error("Failed to update user");
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    try {
      await api.delete(`/admin/users/${userToDelete.id}`);
      toast.success("User deleted");
      setDeleteDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error("Failed to delete user");
    }
  };

  const openCreateUserDialog = () => {
    setNewUser({ full_name: "", email: "", password: "", role: "", units: [], restaurants: [], default_technician_id: "" });
    setCreateUserDialogOpen(true);
  };

  const handleCreateUser = async () => {
    if (!newUser.full_name.trim() || !newUser.email.trim() || !newUser.password.trim()) {
      toast.error("Name, email and password are required");
      return;
    }
    if (newUser.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setCreatingUser(true);
    try {
      await api.post("/admin/users", {
        full_name: newUser.full_name.trim(),
        email: newUser.email.trim().toLowerCase(),
        password: newUser.password,
        role: newUser.role || null,
        units: newUser.units || [],
        restaurants: newUser.restaurants || [],
        default_technician_id: newUser.default_technician_id || null,
        status: "active",
      });
      toast.success(`User ${newUser.email} created and activated`);
      setCreateUserDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    } finally {
      setCreatingUser(false);
    }
  };

  const seedDatabase = async () => {
    try {
      await api.post("/seed");
      toast.success("Database seeded with demo data");
      fetchUsers();
    } catch (error) {
      toast.error("Failed to seed database");
    }
  };

  // Approver Management
  const openApproverDialog = (approver = null, defaultRole = "") => {
    setSelectedApprover(approver);
    if (approver) {
      setEditingApprover({
        role: approver.role,
        name: approver.name || "",
        email: approver.email || ""
      });
    } else {
      setEditingApprover({ role: defaultRole, name: "", email: "" });
    }
    setApproverDialogOpen(true);
  };

  const saveApprover = async () => {
    if (!editingApprover.role || !editingApprover.email) {
      toast.error("Please fill in role and email");
      return;
    }
    
    setEmailsLoading(true);
    try {
      if (selectedApprover?.id) {
        await api.put(`/admin/approvers/${selectedApprover.id}`, {
          name: editingApprover.name,
          email: editingApprover.email
        });
        toast.success("Approver updated");
      } else {
        await api.post("/admin/approvers", editingApprover);
        toast.success("Approver added");
      }
      setApproverDialogOpen(false);
      fetchApproverEmails();
    } catch (error) {
      toast.error("Failed to save approver");
    } finally {
      setEmailsLoading(false);
    }
  };

  const deleteApprover = async () => {
    if (!approverToDelete?.id) return;
    
    try {
      await api.delete(`/admin/approvers/${approverToDelete.id}`);
      toast.success("Approver removed");
      setDeleteApproverDialogOpen(false);
      fetchApproverEmails();
    } catch (error) {
      toast.error("Failed to delete approver");
    }
  };

  const toggleApproverActive = async (approver) => {
    try {
      await api.put(`/admin/approvers/${approver.id}`, { active: !approver.active });
      toast.success(approver.active ? "Approver deactivated" : "Approver activated");
      fetchApproverEmails();
    } catch (error) {
      toast.error("Failed to update approver");
    }
  };

  const APPROVER_ROLES = [
    "SystemAdmin", "HelpDesk", "District Manager", "District Operations", "Finance", "President", "Treasurer"
  ];

  // Email Template Management
  const openTemplateDialog = (templateId) => {
    const template = emailTemplates[templateId];
    setSelectedTemplate(templateId);
    setEditingTemplate({
      subject: template?.subject || "",
      body: template?.body || ""
    });
    setTemplateDialogOpen(true);
  };

  const saveEmailTemplate = async () => {
    try {
      await api.put(`/admin/email-templates/${selectedTemplate}`, editingTemplate);
      toast.success("Template saved");
      setTemplateDialogOpen(false);
      fetchEmailTemplates();
    } catch (error) {
      toast.error("Failed to save template");
    }
  };

  // Business Unit Management
  const openUnitDialog = (unit = null) => {
    setSelectedUnit(unit);
    if (unit) {
      setEditingUnit({
        name: unit.name,
        full_name: unit.full_name,
        color: unit.color,
        icon: unit.icon || "utensils"
      });
    } else {
      setEditingUnit({ name: "", full_name: "", color: "#4F46E5", icon: "utensils" });
    }
    setUnitDialogOpen(true);
  };

  const saveBusinessUnit = async () => {
    try {
      if (selectedUnit) {
        await api.put(`/units/${selectedUnit.id}`, editingUnit);
        toast.success("Unit updated");
      } else {
        await api.post("/units", editingUnit);
        toast.success("Unit created");
      }
      setUnitDialogOpen(false);
      fetchBusinessUnits();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save unit");
    }
  };

  const toggleUnitActive = async (unit) => {
    try {
      await api.put(`/units/${unit.id}`, { active: !unit.active });
      toast.success(unit.active ? "Unit deactivated" : "Unit activated");
      fetchBusinessUnits();
    } catch (error) {
      toast.error("Failed to update unit");
    }
  };

  const TEMPLATE_NAMES = {
    template_ticket_created: "Approval Request",
    template_dm_approved: "DM Approved - DO Needed",
    template_finance_approved: "Finance Approved - President Needed",
    template_final_approval: "Final Approval (Treasurer)",
    template_rejected: "Ticket Rejected",
    template_user_registered: "New User Registration",
    template_user_activated: "Account Activated",
    template_user_rejected: "Account Rejected"
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/select-market")}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
              data-testid="back-to-markets"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center">
                <Wrench className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Admin Panel</h1>
                <p className="text-xs text-gray-500">System Configuration</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-7 mb-8 h-auto flex-wrap">
            <TabsTrigger value="market-config" className="flex items-center gap-1 text-xs sm:text-sm">
              <DollarSign className="w-4 h-4" />
              <span className="hidden sm:inline">Market Config</span>
            </TabsTrigger>
            <TabsTrigger value="branding" className="flex items-center gap-1 text-xs sm:text-sm" data-testid="tab-branding">
              <Image className="w-4 h-4" />
              <span className="hidden sm:inline">Branding</span>
            </TabsTrigger>
            <TabsTrigger value="smtp" className="flex items-center gap-1 text-xs sm:text-sm">
              <Server className="w-4 h-4" />
              <span className="hidden sm:inline">SMTP</span>
            </TabsTrigger>
            <TabsTrigger value="templates" className="flex items-center gap-1 text-xs sm:text-sm">
              <FileText className="w-4 h-4" />
              <span className="hidden sm:inline">Templates</span>
            </TabsTrigger>
            <TabsTrigger value="units" className="flex items-center gap-1 text-xs sm:text-sm">
              <Building2 className="w-4 h-4" />
              <span className="hidden sm:inline">Units</span>
            </TabsTrigger>
            <TabsTrigger value="users" className="flex items-center gap-1 text-xs sm:text-sm">
              <Users className="w-4 h-4" />
              <span className="hidden sm:inline">Users</span>
            </TabsTrigger>
            <TabsTrigger value="logs" className="flex items-center gap-1 text-xs sm:text-sm" onClick={() => navigate("/admin/audit-logs")}>
              <ScrollText className="w-4 h-4" />
              <span className="hidden sm:inline">Logs</span>
            </TabsTrigger>
          </TabsList>

          {/* Market Config Tab (replaces Thresholds + Approvers) */}
          <TabsContent value="market-config">
            <MarketConfig />
          </TabsContent>

          {/* Branding Tab */}
          <TabsContent value="branding">
            <BrandingPanel />
          </TabsContent>

          {/* SMTP Tab */}
          <TabsContent value="smtp">
            <Card className="bg-white border-gray-200">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-gray-900">
                  SMTP Configuration
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="sender_name">Sender Name</Label>
                    <Input
                      id="sender_name"
                      value={smtpSettings.sender_name}
                      onChange={(e) => setSmtpSettings(prev => ({ ...prev, sender_name: e.target.value }))}
                      placeholder="Efacilicare Notifications"
                      data-testid="smtp-sender-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="sender_email">Sender Email</Label>
                    <Input
                      id="sender_email"
                      type="email"
                      value={smtpSettings.sender_email}
                      onChange={(e) => setSmtpSettings(prev => ({ ...prev, sender_email: e.target.value }))}
                      placeholder="notifications@example.com"
                      data-testid="smtp-sender-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="smtp_host">SMTP Host</Label>
                    <Input
                      id="smtp_host"
                      value={smtpSettings.smtp_host}
                      onChange={(e) => setSmtpSettings(prev => ({ ...prev, smtp_host: e.target.value }))}
                      placeholder="smtp.gmail.com"
                      data-testid="smtp-host"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="smtp_port">SMTP Port</Label>
                    <Input
                      id="smtp_port"
                      type="number"
                      value={smtpSettings.smtp_port}
                      onChange={(e) => setSmtpSettings(prev => ({ ...prev, smtp_port: parseInt(e.target.value) }))}
                      placeholder="587"
                      data-testid="smtp-port"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="smtp_username">SMTP Username</Label>
                    <Input
                      id="smtp_username"
                      value={smtpSettings.smtp_username}
                      onChange={(e) => setSmtpSettings(prev => ({ ...prev, smtp_username: e.target.value }))}
                      placeholder="your@gmail.com"
                      data-testid="smtp-username"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="smtp_password">SMTP Password (App Password)</Label>
                    <div className="relative">
                      <Input
                        id="smtp_password"
                        type={showSmtpPassword ? "text" : "password"}
                        value={smtpSettings.smtp_password}
                        onChange={(e) => setSmtpSettings(prev => ({ ...prev, smtp_password: e.target.value }))}
                        placeholder="••••••••"
                        className="pr-10"
                        data-testid="smtp-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowSmtpPassword(!showSmtpPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showSmtpPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500">
                      For Gmail, use an App Password. <a href="https://support.google.com/accounts/answer/185833" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">Learn more</a>
                    </p>
                  </div>
                </div>

                <div className="pt-4 flex gap-3">
                  <Button
                    onClick={saveSmtpSettings}
                    disabled={smtpLoading}
                    className="bg-indigo-600 hover:bg-indigo-700"
                    data-testid="save-smtp-btn"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {smtpLoading ? "Saving..." : "Save Settings"}
                  </Button>
                  <Button
                    onClick={testSmtp}
                    disabled={smtpLoading}
                    variant="outline"
                    data-testid="test-smtp-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Send Test Email
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <div className="space-y-6">
              {/* Seed Database Button */}
              <Card className="bg-white border-gray-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-gray-900">Seed Demo Data</h4>
                      <p className="text-sm text-gray-500">Create demo users and sample tickets for testing</p>
                    </div>
                    <Button onClick={seedDatabase} variant="outline" data-testid="seed-database-btn">
                      <Database className="w-4 h-4 mr-2" />
                      Seed Database
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Pending Users */}
              <Card className="bg-white border-gray-200">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    Pending Users
                    {pendingUsers.length > 0 && (
                      <Badge className="bg-orange-100 text-orange-700">{pendingUsers.length}</Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {usersLoading ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
                    </div>
                  ) : pendingUsers.length > 0 ? (
                    <div className="space-y-3">
                      {pendingUsers.map((u) => (
                        <div key={u.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                          <div>
                            <p className="font-medium text-gray-900">{u.full_name}</p>
                            <p className="text-sm text-gray-600">{u.email}</p>
                            <p className="text-xs text-gray-500">
                              Registered: {format(new Date(u.created_at), "MMM d, yyyy")}
                              {u.preferred_unit && ` • Preferred: ${u.preferred_unit}`}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleEditUser(u)}
                              className="bg-green-600 hover:bg-green-700"
                              data-testid={`activate-user-${u.id}`}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Activate
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => {
                                setUserToDelete(u);
                                setDeleteDialogOpen(true);
                              }}
                              data-testid={`reject-user-${u.id}`}
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              Reject
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">No pending users</p>
                  )}
                </CardContent>
              </Card>

              {/* Active Users */}
              <Card className="bg-white border-gray-200">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg font-semibold text-gray-900">
                    Active Users ({activeUsers.length})
                  </CardTitle>
                  <Button
                    onClick={openCreateUserDialog}
                    size="sm"
                    className="bg-indigo-600 hover:bg-indigo-700"
                    data-testid="create-user-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create User
                  </Button>
                </CardHeader>
                <CardContent>
                  {activeUsers.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Name</th>
                            <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Email</th>
                            <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Role</th>
                            <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Units</th>
                            <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Restaurants</th>
                            <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Default Tech</th>
                            <th className="text-right py-3 px-4 text-xs font-medium text-gray-500 uppercase">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {activeUsers.map((u) => {
                            const userRestaurants = u.restaurants || [];
                            const hasScope = userRestaurants.length > 0;
                            const defaultTech = u.default_technician_id
                              ? technicians.find((t) => t.id === u.default_technician_id)
                              : null;
                            return (
                            <tr key={u.id} className="border-b border-gray-100">
                              <td className="py-3 px-4 font-medium text-gray-900">{u.full_name}</td>
                              <td className="py-3 px-4 text-gray-600">{u.email}</td>
                              <td className="py-3 px-4">
                                <Badge variant="secondary">{u.role}</Badge>
                              </td>
                              <td className="py-3 px-4">
                                {u.units?.map(unit => (
                                  <Badge key={unit} variant="outline" className="mr-1">{unit}</Badge>
                                ))}
                              </td>
                              <td className="py-3 px-4 align-top">
                                {hasScope ? (
                                  <div
                                    className="flex flex-col gap-1 max-w-[260px]"
                                    title={userRestaurants.join(", ")}
                                  >
                                    <span className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 w-fit">
                                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                                      Scoped — {userRestaurants.length}
                                      {userRestaurants.length === 1 ? " store" : " stores"}
                                    </span>
                                    <div className="text-[11px] text-gray-600 leading-snug truncate">
                                      {userRestaurants.slice(0, 2).join(", ")}
                                      {userRestaurants.length > 2 && (
                                        <span className="text-gray-400">
                                          {" "}+{userRestaurants.length - 2} more
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                ) : (
                                  <span className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-gray-200">
                                    <span className="w-1.5 h-1.5 rounded-full bg-gray-400"></span>
                                    All stores
                                  </span>
                                )}
                              </td>
                              <td className="py-3 px-4 align-top">
                                {defaultTech ? (
                                  <span
                                    className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200"
                                    title={`${defaultTech.full_name} (${defaultTech.email})`}
                                    data-testid={`default-tech-${u.id}`}
                                  >
                                    <span className="w-1.5 h-1.5 rounded-full bg-indigo-500"></span>
                                    {defaultTech.full_name}
                                  </span>
                                ) : u.default_technician_id ? (
                                  <span className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                                    Inactive tech
                                  </span>
                                ) : (
                                  <span className="text-[11px] text-gray-400">—</span>
                                )}
                              </td>
                              <td className="py-3 px-4 text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleEditUser(u)}
                                    data-testid={`edit-user-${u.id}`}
                                  >
                                    <Edit className="w-3.5 h-3.5" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => {
                                      setUserToDelete(u);
                                      setDeleteDialogOpen(true);
                                    }}
                                    className="text-red-600 hover:bg-red-50 hover:text-red-700"
                                    data-testid={`delete-user-${u.id}`}
                                    disabled={u.id === user?.id}
                                    title={u.id === user?.id ? "You can't delete your own account" : "Delete user"}
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          )})}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">No active users</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Email Templates Tab */}
          <TabsContent value="templates">
            <Card className="bg-white border-gray-200">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-gray-900">
                  Email Templates
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(TEMPLATE_NAMES).map(([templateId, name]) => (
                    <div
                      key={templateId}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                    >
                      <div>
                        <p className="font-medium text-gray-900">{name}</p>
                        <p className="text-sm text-gray-500">{templateId}</p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openTemplateDialog(templateId)}
                        data-testid={`edit-template-${templateId}`}
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Business Units Tab */}
          <TabsContent value="units">
            <Card className="bg-white border-gray-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg font-semibold text-gray-900">
                  Business Units
                </CardTitle>
                <Button onClick={() => openUnitDialog()} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-unit-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Unit
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {businessUnits.map((unit) => (
                    <div
                      key={unit.id}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center gap-4">
                        <div
                          className="w-10 h-10 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: unit.color + "20" }}
                        >
                          <Building2 className="w-5 h-5" style={{ color: unit.color }} />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{unit.name}</p>
                          <p className="text-sm text-gray-500">{unit.full_name}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={unit.active !== false ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}>
                          {unit.active !== false ? "Active" : "Inactive"}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openUnitDialog(unit)}
                          data-testid={`edit-unit-${unit.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleUnitActive(unit)}
                          data-testid={`toggle-unit-${unit.id}`}
                        >
                          {unit.active !== false ? (
                            <XCircle className="w-4 h-4 text-red-500" />
                          ) : (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          )}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Email Template Dialog */}
      <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Template: {TEMPLATE_NAMES[selectedTemplate]}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Subject Line</Label>
              <Input
                value={editingTemplate.subject}
                onChange={(e) => setEditingTemplate(prev => ({ ...prev, subject: e.target.value }))}
                placeholder="Email subject..."
                data-testid="template-subject-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Email Body (HTML)</Label>
              <Textarea
                value={editingTemplate.body}
                onChange={(e) => setEditingTemplate(prev => ({ ...prev, body: e.target.value }))}
                placeholder="<h2>Title</h2><p>Content...</p>"
                rows={10}
                className="font-mono text-sm"
                data-testid="template-body-input"
              />
              <p className="text-xs text-gray-500">
                Available variables: {"{job_number}"}, {"{restaurant_name}"}, {"{cost}"}, {"{user_name}"}, {"{user_email}"}, {"{role}"}, {"{app_url}"}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTemplateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveEmailTemplate} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-template-btn">
              Save Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Business Unit Dialog */}
      <Dialog open={unitDialogOpen} onOpenChange={setUnitDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedUnit ? "Edit Business Unit" : "Add Business Unit"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Short Name *</Label>
              <Input
                value={editingUnit.name}
                onChange={(e) => setEditingUnit(prev => ({ ...prev, name: e.target.value }))}
                placeholder="QNC"
                disabled={!!selectedUnit}
                data-testid="unit-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Full Name *</Label>
              <Input
                value={editingUnit.full_name}
                onChange={(e) => setEditingUnit(prev => ({ ...prev, full_name: e.target.value }))}
                placeholder="Quick N Clean Operations"
                data-testid="unit-fullname-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Brand Color</Label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={editingUnit.color}
                  onChange={(e) => setEditingUnit(prev => ({ ...prev, color: e.target.value }))}
                  className="w-12 h-10 rounded border cursor-pointer"
                  data-testid="unit-color-input"
                />
                <Input
                  value={editingUnit.color}
                  onChange={(e) => setEditingUnit(prev => ({ ...prev, color: e.target.value }))}
                  className="flex-1"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUnitDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveBusinessUnit} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-unit-btn">
              {selectedUnit ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedUser?.status === "pending" ? "Activate User" : "Edit User"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <p className="font-medium text-gray-900">{selectedUser?.full_name}</p>
              <p className="text-sm text-gray-600">{selectedUser?.email}</p>
            </div>
            
            <div className="space-y-2">
              <Label>Role</Label>
              <Select
                value={editingUser.role}
                onValueChange={(v) => setEditingUser(prev => ({ ...prev, role: v }))}
              >
                <SelectTrigger data-testid="user-role-select">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {(availableRoles.length > 0 ? availableRoles : USER_ROLES).map(role => (
                    <SelectItem key={role} value={role}>{role}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Business Units</Label>
              <div className="space-y-2">
                {(businessUnits.length > 0
                  ? businessUnits.filter((u) => u.active !== false)
                  : BUSINESS_UNITS
                ).map((unit) => {
                  const unitId = unit.id;
                  return (
                    <label key={unitId} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={editingUser.units.includes(unitId)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setEditingUser(prev => ({ ...prev, units: [...prev.units, unitId] }));
                          } else {
                            setEditingUser(prev => ({
                              ...prev,
                              units: prev.units.filter(u => u !== unitId),
                              // Drop any selected restaurants that belonged to this unit
                              // (we don't know exact mapping here without an extra call;
                              // safest is to let backend ignore unknowns)
                            }));
                          }
                        }}
                        className="rounded border-gray-300"
                        data-testid={`unit-checkbox-${unitId}`}
                      />
                      <span className="text-sm text-gray-700">
                        <span className="font-medium">{unit.name || unitId}</span>
                        {unit.full_name && unit.full_name !== unit.name && (
                          <span className="text-gray-500"> — {unit.full_name}</span>
                        )}
                      </span>
                    </label>
                  );
                })}
                {businessUnits.length === 0 && (
                  <p className="text-xs text-amber-600">
                    Showing legacy defaults. Once markets are added in the Markets tab, they will appear here automatically.
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Restaurants (optional scope)</Label>
              <RestaurantPicker
                selectedUnits={editingUser.units}
                selected={editingUser.restaurants || []}
                onChange={(arr) => setEditingUser((prev) => ({ ...prev, restaurants: arr }))}
              />
            </div>

            <div className="space-y-2">
              <Label>Default Technician (optional)</Label>
              <p className="text-xs text-gray-500">
                If set, tickets this user creates auto-assign to this tech and only they get emailed. Falls back to the market pool if the tech is inactive.
              </p>
              <Select
                value={editingUser.default_technician_id || "__none__"}
                onValueChange={(v) =>
                  setEditingUser((prev) => ({
                    ...prev,
                    default_technician_id: v === "__none__" ? "" : v,
                  }))
                }
              >
                <SelectTrigger data-testid="edit-user-default-tech">
                  <SelectValue placeholder="No default technician" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No default (use market pool)</SelectItem>
                  {technicians
                    .filter((t) => {
                      const tu = t.units || [];
                      if (!editingUser.units || editingUser.units.length === 0) return true;
                      if (tu.length === 0) return true;
                      return tu.some((x) => editingUser.units.includes(x));
                    })
                    .map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.full_name} — {t.email}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUserDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveUser} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-user-btn">
              {selectedUser?.status === "pending" ? "Activate & Save" : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {userToDelete?.status === "pending" ? "Reject User Registration?" : "Delete User Account?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {userToDelete?.status === "pending"
                ? `This will permanently delete the registration request for ${userToDelete?.full_name} (${userToDelete?.email}). They will receive a rejection notification email.`
                : `This will permanently remove ${userToDelete?.full_name} (${userToDelete?.email}) and all their access. This action cannot be undone.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteUser} className="bg-red-600 hover:bg-red-700">
              {userToDelete?.status === "pending" ? "Reject & Delete" : "Delete User"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create User Dialog (admin-bypass for self-registration) */}
      <Dialog open={createUserDialogOpen} onOpenChange={setCreateUserDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="create-user-dialog">
          <DialogHeader>
            <DialogTitle>Create New User</DialogTitle>
            <DialogDescription>
              Create an account directly. The user will be active immediately and
              can log in with the credentials you set here.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label htmlFor="cu-name">Full Name</Label>
              <Input
                id="cu-name"
                value={newUser.full_name}
                onChange={(e) => setNewUser((u) => ({ ...u, full_name: e.target.value }))}
                placeholder="Jane Doe"
                data-testid="create-user-name"
              />
            </div>
            <div>
              <Label htmlFor="cu-email">Email</Label>
              <Input
                id="cu-email"
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser((u) => ({ ...u, email: e.target.value }))}
                placeholder="jane@example.com"
                data-testid="create-user-email"
              />
            </div>
            <div>
              <Label htmlFor="cu-password">Temporary Password</Label>
              <Input
                id="cu-password"
                type="text"
                value={newUser.password}
                onChange={(e) => setNewUser((u) => ({ ...u, password: e.target.value }))}
                placeholder="At least 6 characters"
                data-testid="create-user-password"
              />
              <p className="text-[11px] text-gray-500 mt-1">
                Share this password with the user — they can change it later from their profile.
              </p>
            </div>
            <div>
              <Label htmlFor="cu-role">Role</Label>
              <Select
                value={newUser.role}
                onValueChange={(v) => setNewUser((u) => ({ ...u, role: v }))}
              >
                <SelectTrigger id="cu-role" data-testid="create-user-role">
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  {(availableRoles.length > 0 ? availableRoles : USER_ROLES).map((role) => (
                    <SelectItem key={role} value={role}>{role}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Markets / Units</Label>
              <div className="grid grid-cols-2 gap-2 mt-1.5">
                {(businessUnits.length > 0 ? businessUnits : BUSINESS_UNITS).map((bu) => {
                  const id = bu.name || bu.id;
                  const checked = newUser.units.includes(id);
                  return (
                    <label
                      key={id}
                      className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer text-sm ${
                        checked ? "border-indigo-500 bg-indigo-50 text-indigo-900" : "border-gray-200 text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) =>
                          setNewUser((u) => ({
                            ...u,
                            units: e.target.checked
                              ? [...u.units, id]
                              : u.units.filter((x) => x !== id),
                          }))
                        }
                        className="h-4 w-4 accent-indigo-600"
                        data-testid={`create-user-unit-${id}`}
                      />
                      <span>{bu.full_name || bu.name || id}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div>
              <Label>Restaurants (optional scope)</Label>
              <RestaurantPicker
                selectedUnits={newUser.units}
                selected={newUser.restaurants || []}
                onChange={(arr) => setNewUser((u) => ({ ...u, restaurants: arr }))}
              />
            </div>

            <div>
              <Label>Default Technician (optional)</Label>
              <p className="text-xs text-gray-500 mt-1">
                If set, tickets this user creates auto-assign to this tech. Falls back to the market pool if inactive.
              </p>
              <Select
                value={newUser.default_technician_id || "__none__"}
                onValueChange={(v) =>
                  setNewUser((u) => ({
                    ...u,
                    default_technician_id: v === "__none__" ? "" : v,
                  }))
                }
              >
                <SelectTrigger data-testid="create-user-default-tech" className="mt-1.5">
                  <SelectValue placeholder="No default technician" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No default (use market pool)</SelectItem>
                  {technicians
                    .filter((t) => {
                      const tu = t.units || [];
                      if (!newUser.units || newUser.units.length === 0) return true;
                      if (tu.length === 0) return true;
                      return tu.some((x) => newUser.units.includes(x));
                    })
                    .map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.full_name} — {t.email}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateUserDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateUser}
              disabled={creatingUser}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="create-user-submit"
            >
              {creatingUser ? "Creating..." : "Create User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
